/* NC made-to-measure rug - pricing engine and unit formatting (pure, no DOM, testable)
 * naturescollection.us rules (user decision 2026-10-02):
 *  - $89 per sq ft, same price for every wool type and color, no minimum price, no tiers
 *  - customers enter whole inches; min 40 in, max 390 in per side (limits come from the section settings)
 *  - round rugs are priced as the enclosing square: Ø × Ø
 * Modes:
 *  - "quantity": price unit = 0.1 sq ft = 14.4 in². quantity = round(in² / 14.4), half up.
 *                Unit price = variant price ($8.90). Shown price = quantity × unit price = exactly the checkout price.
 *  - "function": quantity 1, price set server-side by a Shopify Function: round(in² × price per sq ft / 144).
 */
(function (root) {
  var IN2_PER_SQFT = 144;
  var IN2_PER_UNIT = 14.4;   // one cart unit = 0.1 sq ft
  var UNITS_PER_SQFT = 10;
  var CM_PER_IN = 2.54;
  var M_PER_IN = 0.0254;

  function areaIn2(shape, w, l, d) {
    return shape === 'round' ? d * d : w * l;
  }

  /* a / b rounded half up, for non-negative a and positive integer b, in integer arithmetic.
     Plain Math.round(in2 / 14.4) works too, but 14.4 has no exact binary form; doing it on
     integers guarantees that exact .5 cases (e.g. 42 × 42 in = 1764 in² = 122.5 units) always round up. */
  function roundDiv(a, b) {
    var n = 2 * Math.round(a) + b, q = 2 * b;
    return (n - n % q) / q;
  }

  /* in² -> cart units of 0.1 sq ft: round(in² / 14.4) = round(in² × 10 / 144) */
  function unitsFor(in2) {
    return roundDiv(in2 * UNITS_PER_SQFT, IN2_PER_SQFT);
  }

  function calc(opts) {
    var in2 = areaIn2(opts.shape, opts.w, opts.l, opts.d);
    var res = { in2: in2 };
    if (opts.mode === 'function') {
      res.quantity = 1;
      res.totalCents = roundDiv(in2 * opts.pricePerSqftCents, IN2_PER_SQFT);
      res.sqft = in2 / IN2_PER_SQFT;
    } else {
      var units = unitsFor(in2);
      res.quantity = units;
      res.totalCents = units * opts.unitPriceCents;
      res.sqft = units / UNITS_PER_SQFT; // exactly the billed area
    }
    return res;
  }

  function validate(v, min, max) {
    if (v === '' || v === null || v === undefined) return 'empty';
    var n = Number(v);
    if (!isFinite(n)) return 'nan';
    if (Math.floor(n) !== n) return 'int';
    if (n < min) return 'min';
    if (n > max) return 'max';
    return null;
  }

  /* ---------- formatting ---------- */
  function numStr(n) { // 71 -> "71", 98.5 -> "98.5"
    return String(Math.round(n * 100) / 100);
  }
  /* one length in feet: 72 -> "6'", 79 -> "6' 7\"" */
  function ftIn(inches) {
    var n = Math.round(inches), ft = Math.floor(n / 12), r = n - ft * 12;
    return r ? ft + "' " + r + '"' : ft + "'";
  }
  function wholeFt(inches) { return Math.round(inches) % 12 === 0; }
  /* feet part: "6 × 9 ft", "6 ft", "6' 7\" × 9' 10\"", "5' 11\"" */
  function ftText(shape, w, l, d) {
    if (shape === 'round') return wholeFt(d) ? Math.round(d) / 12 + ' ft' : ftIn(d);
    if (wholeFt(w) && wholeFt(l)) return Math.round(w) / 12 + ' × ' + Math.round(l) / 12 + ' ft';
    return ftIn(w) + ' × ' + ftIn(l);
  }
  /* inch part: "72 × 108 in", "Ø 72 in" */
  function inText(shape, w, l, d) {
    return shape === 'round' ? 'Ø ' + numStr(d) + ' in' : numStr(w) + ' × ' + numStr(l) + ' in';
  }
  /* full: "72 × 108 in (6 × 9 ft)", "Ø 72 in (6 ft)" */
  function dimsText(shape, w, l, d) {
    return inText(shape, w, l, d) + ' (' + ftText(shape, w, l, d) + ')';
  }
  /* one side for drawings: "72 in (6 ft)", "79 in (6' 7\")" */
  function lenText(inches) {
    return numStr(inches) + ' in (' + (wholeFt(inches) ? Math.round(inches) / 12 + ' ft' : ftIn(inches)) + ')';
  }
  /* metric size for the Danish workshop: "183 × 274 cm", "Ø 183 cm" */
  function cmText(shape, w, l, d) {
    var cm = function (x) { return Math.round(x * CM_PER_IN); };
    return shape === 'round' ? 'Ø ' + cm(d) + ' cm' : cm(w) + ' × ' + cm(l) + ' cm';
  }
  function fmtNum(n, dec, locale) {
    try {
      return new Intl.NumberFormat(locale || 'en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
    } catch (e) { return n.toFixed(dec); }
  }
  /* "54.0 sq ft" */
  function sqftText(sqft, locale) { return fmtNum(sqft, 1, locale) + ' sq ft'; }
  /* "$4,806.00"; short = drop ".00" on whole amounts ("$89") */
  function fmtMoney(cents, locale, currency, short) {
    var whole = short && cents % 100 === 0;
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency', currency: currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
      }).format(cents / 100);
    } catch (e) {
      var s = (cents / 100).toFixed(whole ? 0 : 2);
      return (currency && currency !== 'USD') ? s + ' ' + currency : '$' + s;
    }
  }

  var api = {
    IN2_PER_UNIT: IN2_PER_UNIT, IN2_PER_SQFT: IN2_PER_SQFT, CM_PER_IN: CM_PER_IN, M_PER_IN: M_PER_IN,
    areaIn2: areaIn2, roundDiv: roundDiv, unitsFor: unitsFor, calc: calc, validate: validate,
    ftIn: ftIn, ftText: ftText, inText: inText, dimsText: dimsText, lenText: lenText, cmText: cmText,
    fmtNum: fmtNum, sqftText: sqftText, fmtMoney: fmtMoney
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.NcTnmPrice = api;
})(typeof window !== 'undefined' ? window : globalThis);
